import { isSameDay, startOfToday } from 'date-fns'
import { formatDate, formatShortDate } from '@/lib/utils'
import { FILTER_DEFAULTS, type LocationSearchParams } from '../../hooks/useLocatiomSearchParams'
import type { SearchFilters } from '@/lib/track'
import { parseCategorySlugs } from './categoryOptions'
import { parseFacilityKeys } from './facilityOptions'

export interface Region {
  id: number
  name: string
  slug: string
}

export function getRegionName(regionSlug: string | undefined, regions: Region[] | undefined): string {
  if (!regionSlug || regionSlug === 'all') {
    return 'Kamkoliv'
  }
  return regions?.find((r) => r.slug === regionSlug)?.name ?? 'Kamkoliv'
}

export function formatBedsAndCribs(beds: number, cribs: number): string {
  const bedsText = `${beds} ${beds === 1 ? 'Dospělý' : 'Dospělí'}`
  const cribsText = `${cribs} ${cribs === 1 ? 'Dítě' : 'Dětí'}`
  return `${bedsText} + ${cribsText}`
}

export function formatDateRange(fromDate: Date | undefined, toDate: Date | undefined): string {
  if (fromDate && toDate) {
    return `${formatShortDate(fromDate)} – ${formatDate(toDate)}`
  }
  if (fromDate) {
    return formatDate(fromDate)
  }
  if (toDate) {
    return `do ${formatDate(toDate)}`
  }
  return 'Libovolný termín'
}

export function hasActiveFilters(filters: SearchFilters | LocationSearchParams): boolean {
  const fromDateActive = filters.fromDate instanceof Date && !isSameDay(filters.fromDate, startOfToday())
  return (
    fromDateActive ||
    (filters.region !== undefined && filters.region !== FILTER_DEFAULTS.region) ||
    filters.labelId !== undefined ||
    (filters.beds !== undefined && filters.beds !== FILTER_DEFAULTS.beds) ||
    (filters.cribs !== undefined && filters.cribs !== FILTER_DEFAULTS.cribs) ||
    filters.adult === true ||
    filters.pets === true ||
    filters.facility !== undefined ||
    filters.category !== undefined
  )
}

/**
 * Number of narrowing filters for the mobile "Filtry · n" indicator: each
 * selected category and facility counts on its own, any other filter as one.
 * Region, dates and guests are left out, the summary bar shows them already.
 */
export function countActiveFilters(filters: LocationSearchParams): number {
  return (
    parseCategorySlugs(filters.category).length +
    parseFacilityKeys(filters.facility).length +
    [filters.labelId !== undefined, filters.cribs !== FILTER_DEFAULTS.cribs, filters.adult, filters.pets].filter(
      Boolean,
    ).length
  )
}

export function formatSearchSummary(
  filters: LocationSearchParams,
  regions: Region[] | undefined,
): {
  location: string
  date: string
  people: string
} {
  return {
    location: getRegionName(filters.region, regions),
    date: formatDateRange(filters.fromDate, filters.toDate),
    people: formatBedsAndCribs(filters.beds, filters.cribs),
  }
}
